import { useEffect, useState } from "react";
import { api, apiError } from "../lib/api";
import { ChipGroup } from "./fields";
import { X, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

export default function BehaviorSheet({ student, onClose, onSaved }) {
  const [tax, setTax] = useState(null);
  const [form, setForm] = useState({ behavior_type: "", prompt_used: "Verbal", duration_minutes: 5, note: "", alert_category: "Routine update" });
  const [saving, setSaving] = useState(false);
  const [incidentStep, setIncidentStep] = useState(null);
  const [incForm, setIncForm] = useState({ description: "", location: "", others_involved: "", injuries: "None", admin_notified: "", follow_up: "" });
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => { api.get("/meta/taxonomies").then((r) => setTax(r.data)); }, []);

  const submit = async () => {
    if (!form.behavior_type) { toast.error("Select a behavior type"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/behavior-incidents", {
        ...form, student_id: student.id, date: new Date().toISOString().slice(0, 10),
        time: new Date().toTimeString().slice(0, 5),
      });
      if (data.incident_form_required) {
        setIncidentStep(data);
        setIncForm((f) => ({ ...f, description: form.note }));
      } else {
        toast.success("Behavior logged");
        onSaved();
      }
    } catch (e) { toast.error(apiError(e)); }
    setSaving(false);
  };

  const submitIncidentForm = async () => {
    setSaving(true);
    try {
      await api.post("/form-submissions", {
        form_key: "incident_report", student_id: student.id, date: incidentStep.date,
        data: incForm, linked_incident_id: incidentStep.id,
      });
      toast.success("Incident report completed — coordinator and TAG notified");
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
    setSaving(false);
  };

  if (!tax) return null;
  const allCategories = [...tax.alert_categories.routine, ...tax.alert_categories.elevated, ...tax.alert_categories.critical];

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 sf-overlay flex items-end sm:items-center justify-center" onClick={onClose}>
      <div data-testid="behavior-sheet" className="sf-sheet bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-slate-100 px-5 py-4 flex items-center justify-between z-10">
          <div>
            <h2 className="font-heading font-semibold text-slate-900">{incidentStep ? "Incident Report (required)" : "Log Behavior / Concern"}</h2>
            <p className="text-xs text-slate-400">{student.first_name} {student.last_name}</p>
          </div>
          {!incidentStep && <button data-testid="close-behavior-sheet-btn" onClick={onClose} className="p-2 text-slate-400 hover:text-slate-700"><X size={18} /></button>}
        </div>

        {!incidentStep ? (
          <>
            <div className="p-5 space-y-5">
              <ChipGroup label="Behavior type" options={tax.behavior_types} value={form.behavior_type} onChange={set("behavior_type")} testPrefix="behavior-type" />
              <ChipGroup label="Prompt used" options={tax.prompt_levels} value={form.prompt_used} onChange={set("prompt_used")} testPrefix="prompt-used" />
              <ChipGroup label="Duration (minutes)" options={["1", "5", "10", "15", "30"]} value={String(form.duration_minutes)} onChange={(v) => set("duration_minutes")(Number(v))} testPrefix="duration" />
              <div>
                <p className="text-xs font-bold tracking-[0.06em] uppercase text-slate-500 mb-2">What happened — observable language only</p>
                <textarea data-testid="behavior-note-input" value={form.note} onChange={(e) => set("note")(e.target.value)} rows={2}
                  placeholder="e.g., Left seat three times during independent work; returned after gestural prompt."
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600" />
              </div>
              <div>
                <p className="text-xs font-bold tracking-[0.06em] uppercase text-slate-500 mb-2">Alert category</p>
                <div className="space-y-1.5">
                  {allCategories.map((c) => {
                    const level = tax.alert_categories.critical.includes(c) ? "critical" : tax.alert_categories.elevated.includes(c) ? "elevated" : "routine";
                    return (
                      <button key={c} type="button" data-testid={`alert-cat-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        onClick={() => set("alert_category")(c)}
                        className={`w-full text-left px-3.5 py-2.5 rounded-lg text-sm border transition-colors duration-150 flex items-center justify-between ${
                          form.alert_category === c ? "border-emerald-600 bg-emerald-50 text-emerald-900 font-medium" : "border-slate-200 text-slate-600 hover:bg-slate-50"
                        }`}>
                        {c}
                        <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${level === "critical" ? "bg-red-100 text-red-700" : level === "elevated" ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-500"}`}>{level}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="sticky bottom-0 bg-white border-t border-slate-100 p-4">
              <button data-testid="submit-behavior-btn" onClick={submit} disabled={saving}
                className="w-full bg-emerald-700 hover:bg-emerald-800 text-white font-semibold rounded-full py-3 text-sm transition-colors duration-150 active:scale-[0.98] disabled:opacity-60">
                {saving ? "Saving…" : "Submit"}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="p-5 space-y-4">
              <p className="flex items-start gap-2 text-xs bg-red-50 border border-red-200 text-red-800 rounded-md p-3">
                <ShieldAlert size={16} className="shrink-0 mt-0.5" />
                This alert category requires the school's incident report before the entry can be closed. Coordinator and TAG have been alerted.
              </p>
              {[["description", "Description of incident"], ["location", "Location"], ["others_involved", "Others involved"], ["injuries", "Injuries"], ["admin_notified", "Admin/office notified?"], ["follow_up", "Immediate follow-up taken"]].map(([k, label]) => (
                <div key={k}>
                  <label className="text-xs font-bold tracking-[0.06em] uppercase text-slate-500">{label}</label>
                  <textarea data-testid={`incident-${k}-input`} value={incForm[k]} onChange={(e) => setIncForm((f) => ({ ...f, [k]: e.target.value }))} rows={k === "description" ? 3 : 1}
                    className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600" />
                </div>
              ))}
            </div>
            <div className="sticky bottom-0 bg-white border-t border-slate-100 p-4">
              <button data-testid="submit-incident-form-btn" onClick={submitIncidentForm} disabled={saving || !incForm.description}
                className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold rounded-full py-3 text-sm transition-colors duration-150 active:scale-[0.98] disabled:opacity-60">
                {saving ? "Submitting…" : "Complete incident report"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
